// <StatusChip> — semantic pill chip with a leading inline SVG icon.
//
// Per DESIGN.md §2 (never color alone) and §7.2:
//   - Tones: success | warning | danger | info | mock
//   - subtle bg + strong text + small leading icon.

import type { ReactNode } from "react";
import "./StatusChip.css";

export type StatusTone = "success" | "warning" | "danger" | "info" | "mock";

export interface StatusChipProps {
  tone: StatusTone;
  children: ReactNode;
}

function Icon({ tone }: { tone: StatusTone }): JSX.Element {
  // Inline SVGs (no icon library per DESIGN.md §7.2 / §7.3). 12px grid.
  switch (tone) {
    case "success":
      return (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M2.5 6.2L5 8.7L9.5 3.8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "warning":
      return (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6 1.5L11 10.2H1L6 1.5Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
          <rect x="5.5" y="4.5" width="1" height="3" fill="currentColor" />
          <rect x="5.5" y="8" width="1" height="1" fill="currentColor" />
        </svg>
      );
    case "danger":
      return (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx="6"
            cy="6"
            r="4.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <rect x="5.5" y="3.5" width="1" height="4" fill="currentColor" />
          <rect x="5.5" y="8" width="1" height="1" fill="currentColor" />
        </svg>
      );
    case "info":
      return (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          aria-hidden="true"
          focusable="false"
        >
          <circle
            cx="6"
            cy="6"
            r="4.6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          />
          <rect x="5.5" y="5" width="1" height="3.5" fill="currentColor" />
          <rect x="5.5" y="3.25" width="1" height="1" fill="currentColor" />
        </svg>
      );
    case "mock":
      return (
        <svg
          viewBox="0 0 12 12"
          width="12"
          height="12"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M2.5 9L9.5 2M2.5 2L9.5 9"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      );
  }
}

export function StatusChip({ tone, children }: StatusChipProps): JSX.Element {
  return (
    <span className={`rb-status-chip rb-status-chip--${tone}`}>
      <span className="rb-status-chip__icon">
        <Icon tone={tone} />
      </span>
      <span className="rb-status-chip__label">{children}</span>
    </span>
  );
}
